import React from 'react';
import Markdown from './Markdown';
import ToolChip from './ToolChip';
import ToolTrail from './ToolTrail';
import { isTrailStep } from './trailSteps';
import InlineArtifactImage from './InlineArtifactImage';
import SetupStatus from './SetupStatus';
import type { Message, SetupStep, ToolExecution } from '@hooks/useChat';
import type {
  SkillAccentColorFn as AgentAccentColorFn,
  SkillCategoryFn as AgentCategoryFn,
} from '@constants/skillCategories';
import LegacyAssistantContent from './LegacyAssistantContent';
import { isLegacyMultiAgentMessage } from './legacyMessage';

interface AssistantContentProps {
  message: Message;
  /** The turn is still running; drives the live header of the tool trail. */
  active?: boolean;
  /** When the user sent the message that opened this turn. */
  turnStartedAt?: Date | string;
  /** Live setup progress (skill loads, environment boot); only the last message carries it. */
  setupSteps?: SetupStep[];
  setupExiting?: boolean;
  agentAccentColor?: AgentAccentColorFn;
  agentCategoryFn?: AgentCategoryFn;
  onApproveToolExecution?: (toolUseId: string) => Promise<void>;
  onRejectToolExecution?: (toolUseId: string, comment?: string) => Promise<void>;
}

const positionOf = (tool: ToolExecution, end: number): number => tool.insertPosition ?? end;

// A step with no position or one at the very end (failure, interrupt) must not
// fold the whole answer, so the boundary falls back to the previous step.
const foldBoundary = (steps: ToolExecution[], end: number): number =>
  Math.max(0, ...steps.map((t) => t.insertPosition ?? 0).filter((p) => p < end));

const chipKey = (tool: ToolExecution, prefix: string, idx: number): string =>
  `${prefix}-${tool.toolUseId || `${tool.tool}-${idx}`}`;

/** Text between `from` and `to` with each tool's chip placed at its offset. */
const interleave = (
  content: string,
  from: number,
  to: number,
  tools: ToolExecution[],
  prefix: string,
  chip: (tool: ToolExecution, key: string) => React.ReactNode,
): React.ReactNode[] => {
  const parts: React.ReactNode[] = [];
  let cursor = from;
  tools.forEach((tool, idx) => {
    const pos = Math.min(Math.max(positionOf(tool, to), cursor), to);
    if (pos > cursor) {
      parts.push(<Markdown key={`${prefix}-t-${idx}`}>{content.substring(cursor, pos)}</Markdown>);
    }
    parts.push(chip(tool, chipKey(tool, prefix, idx)));
    cursor = pos;
  });
  if (cursor < to) {
    parts.push(<Markdown key={`${prefix}-tail`}>{content.substring(cursor, to)}</Markdown>);
  }
  return parts;
};

/**
 * Renders assistant content with tool chips interleaved at the correct
 * positions (`insertPosition` = char offset in the text stream). The steps the
 * agent took on the way fold into a collapsed {@link ToolTrail}; only the text
 * after the last step, approval requests and delivered artifacts stay visible.
 * Messages carrying multi-agent metadata only exist on historical
 * conversations and are delegated to {@link LegacyAssistantContent}.
 */
const AssistantContent: React.FC<AssistantContentProps> = ({
  message,
  active = false,
  turnStartedAt,
  setupSteps,
  setupExiting = false,
  agentAccentColor,
  agentCategoryFn,
  onApproveToolExecution,
  onRejectToolExecution,
}) => {
  const { content, toolExecutions } = message;
  const artifacts = message.inlineArtifacts;

  if (isLegacyMultiAgentMessage(message)) {
    return (
      <LegacyAssistantContent
        message={message}
        agentAccentColor={agentAccentColor}
        agentCategoryFn={agentCategoryFn}
        onApproveToolExecution={onApproveToolExecution}
        onRejectToolExecution={onRejectToolExecution}
      />
    );
  }

  const artifactNodes = artifacts?.map((art) => (
    <InlineArtifactImage key={art.artifactId} artifact={art} />
  ));
  const setup = setupSteps?.length ? <SetupStatus steps={setupSteps} exiting={setupExiting} /> : null;

  const end = content.length;
  const chip = (tool: ToolExecution, key: string) => (
    <ToolChip key={key} tool={tool} onApprove={onApproveToolExecution} onReject={onRejectToolExecution} />
  );

  const sorted = [...(toolExecutions ?? [])].sort((a, b) => positionOf(a, end) - positionOf(b, end));
  const trailSteps = sorted.filter(isTrailStep);
  if (trailSteps.length === 0 && !setup) {
    if (!sorted.length && !content && !artifacts?.length) return null;

    return (
      <>
        {interleave(content, 0, end, sorted, 'v', chip)}
        {artifactNodes}
      </>
    );
  }

  const boundary = foldBoundary(trailSteps, end);
  const shown = sorted.filter((t) => !isTrailStep(t));
  const hoisted = shown.filter((t) => positionOf(t, end) < boundary);
  const tail = shown.filter((t) => positionOf(t, end) >= boundary);

  return (
    <>
      <ToolTrail
        steps={trailSteps}
        setupSteps={setupSteps}
        active={active}
        awaitingApproval={shown.some((t) => t.status === 'pending_approval')}
        startedAt={turnStartedAt}
        completedAt={message.completedAt}
      >
        {setup}
        {interleave(content, 0, boundary, trailSteps, 'trail', chip)}
      </ToolTrail>
      {hoisted.map((t, idx) => chip(t, chipKey(t, 'hoist', idx)))}
      {interleave(content, boundary, end, tail, 'tail', chip)}
      {artifactNodes}
    </>
  );
};

export default AssistantContent;
